import React from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Tabs,
  Tab,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Divider
} from '@mui/material';
import { 
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Business as BusinessIcon,
  Save as SaveIcon
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * Material Settings Page
 * 
 * System settings page for administrators
 * TODO: Implement full settings management
 */
export default function MaterialSettingsPage() {
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      {/* Page header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          System Settings
        </Typography>
      </Box>

      {/* Settings tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="settings tabs">
          <Tab icon={<SettingsIcon />} label="General" />
          <Tab icon={<SecurityIcon />} label="Security" />
          <Tab icon={<NotificationsIcon />} label="Notifications" />
          <Tab icon={<BusinessIcon />} label="Company" />
        </Tabs>

        {/* General Settings */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            General Settings
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="System Name"
                defaultValue="ModularCompliance"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Time Zone"
                defaultValue="UTC"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Enable maintenance mode"
              />
            </Grid>
          </Grid>
          <Box mt={3}>
            <Button variant="contained" startIcon={<SaveIcon />}>
              Save General Settings
            </Button>
          </Box>
        </TabPanel>

        {/* Security Settings */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Security Settings
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Enforce two-factor authentication"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Enable password complexity requirements"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Session timeout (minutes)"
                defaultValue="30"
                variant="outlined"
              />
            </Grid>
          </Grid>
          <Box mt={3}>
            <Button variant="contained" startIcon={<SaveIcon />}>
              Save Security Settings
            </Button>
          </Box>
        </TabPanel>

        {/* Notification Settings */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Notification Settings
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Email notifications for task assignments"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch defaultChecked />}
                label="Email notifications for framework updates"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch />}
                label="Daily compliance summary emails"
              />
            </Grid>
          </Grid>
          <Box mt={3}>
            <Button variant="contained" startIcon={<SaveIcon />}>
              Save Notification Settings
            </Button>
          </Box>
        </TabPanel>

        {/* Company Settings */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Company Settings
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company Name"
                defaultValue="Example Corporation"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Industry"
                defaultValue="Technology"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Company Description"
                defaultValue="Leading technology company focused on compliance and security."
                variant="outlined"
              />
            </Grid>
          </Grid>
          <Box mt={3}>
            <Button variant="contained" startIcon={<SaveIcon />}>
              Save Company Settings
            </Button>
          </Box>
        </TabPanel>
      </Paper>

      {/* Coming soon notice */}
      <Box mt={4} p={3} textAlign="center">
        <Typography variant="body1" color="text.secondary">
          Advanced settings features coming soon...
        </Typography>
      </Box>
    </Box>
  );
}
